import React, { useState } from 'react';
import HeroSection from '../components/shared/HeroSection';
import SectionTitle from '../components/shared/SectionTitle';
import { Card, CardContent } from '../components/ui/card';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '../components/ui/carousel';
import {
  Dialog,
  DialogContent,
} from '../components/ui/dialog';
import { heroImages, brands } from '../data/mock';
import { Sun, Leaf, Snowflake, X, ChevronDown } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useCarousel } from '../hooks/useCarousel';

// Fallback images
const fallbackImages = [
  '/assets/images/textile-carousel-1.jpg',
  '/assets/images/textile-carousel-2.jpg',
  '/assets/images/textile-carousel-3.jpg',
  '/assets/images/textile-carousel-4.jpg',
];

const TextilePage = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [expandedBrand, setExpandedBrand] = useState(null);
  const { currentTheme } = useTheme();
  const { images: dbImages } = useCarousel('textile');
  
  // Use database images or fallback
  const textileCarouselImages = dbImages.length > 0 ? dbImages : fallbackImages;

  const toggleBrand = (brandName) => {
    setExpandedBrand(expandedBrand === brandName ? null : brandName);
  };

  const seasons = [
    {
      icon: Sun,
      title: 'Été',
      description: `« Le temps est bon, Le ciel est bleu, Nous n'avons rien à faire, rien que d'être heureux »

L'été, courir peut devenir un vrai défi : chaleur écrasante, soleil de plomb… votre corps travaille dur pour rester à 37 °C !

Dès que le thermomètre dépasse 25 °C, le risque de coup de chaleur augmente. C'est là qu'interviennent les vêtements techniques de running : légers, respirants et conçus pour laisser la peau respirer.

Grâce à leurs matières intelligentes, ils évacuent la transpiration et maintiennent une température corporelle stable. Résultat : vous restez au frais, concentré… et prêt à profiter pleinement de vos sorties estivales, tout en évitant la surchauffe.`,
      cardStyle: 'dark'
    },
    {
      icon: Leaf,
      title: 'Mi-Saison',
      description: `Ni trop chaud, ni trop froid : la mi-saison, c'est souvent le moment préféré des coureurs. Mais attention, elle sait aussi nous surprendre !

Un jour la pluie, le lendemain le soleil, parfois un vent bien installé… Pour rester à l'aise dans toutes ces situations, misez sur des vêtements polyvalents. Légers, respirants et protecteurs, ils s'adaptent à la météo comme à votre rythme — pour que chaque sortie reste un plaisir, quel que soit le temps.

Les textiles techniques de mi-saison allient respirabilité, protection et confort, vous permettant de courir dans toutes les conditions.`,
      cardStyle: 'primary'
    },
    {
      icon: Snowflake,
      title: 'Hiver',
      description: `Contrairement aux idées reçues, l'hiver est une saison idéale pour le running. Tant que les températures ne descendent pas trop bas, le corps bénéficie d'un climat favorable à l'effort : pas de chaleur accablante, pas de surchauffe… juste de la fraîcheur stimulante !

Le vent, la pluie ou le manque de soleil peuvent toutefois donner envie de rester sous la couette. Heureusement, les vêtements techniques d'hiver sont là pour vous protéger et garantir confort et chaleur.

Bien équipé, courir l'hiver devient un vrai plaisir — et une excellente façon de maintenir votre forme toute l'année.`,
      cardStyle: 'light'
    }
  ];

  const getCardStyles = (cardStyle) => {
    if (currentTheme === 'ral6039') {
      switch (cardStyle) {
        case 'dark':
          return {
            bg: 'bg-[#4F5358]',
            text: 'text-white',
            subtext: 'text-gray-200',
            iconBg: 'bg-white/20',
            iconColor: 'text-white'
          };
        case 'primary':
          return {
            bg: 'bg-[#B3C43E]',
            text: 'text-white',
            subtext: 'text-white/90',
            iconBg: 'bg-white/30',
            iconColor: 'text-white'
          };
        case 'light':
          return {
            bg: 'bg-white border-2 border-[#4F5358]',
            text: 'text-gray-900',
            subtext: 'text-gray-600',
            iconBg: 'bg-[#4F5358]',
            iconColor: 'text-white'
          };
        default:
          return {};
      }
    } else {
      // Thème Émeraude
      switch (cardStyle) {
        case 'dark':
          return {
            bg: 'bg-gray-800',
            text: 'text-white',
            subtext: 'text-gray-200',
            iconBg: 'bg-white/20',
            iconColor: 'text-white'
          };
        case 'primary':
          return {
            bg: 'bg-emerald-600',
            text: 'text-white',
            subtext: 'text-emerald-50',
            iconBg: 'bg-white/20',
            iconColor: 'text-white'
          };
        case 'light':
          return {
            bg: 'bg-white border-2 border-gray-300',
            text: 'text-gray-900',
            subtext: 'text-gray-600',
            iconBg: 'bg-emerald-600',
            iconColor: 'text-white'
          };
        default:
          return {};
      }
    }
  };

  return (
    <div className="min-h-screen">
      {/* Lightbox Modal */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-4xl p-0 bg-transparent border-none">
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-2 right-2 z-10 p-2 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Textile running"
              className="w-full h-auto max-h-[85vh] object-contain rounded-lg"
            />
          )}
        </DialogContent>
      </Dialog>

      <HeroSection
        image={heroImages.textile}
        title="Le Textile"
        subtitle="Des vêtements techniques pour le confort par tous les temps"
        height="h-[50vh]"
      />

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Pas de mauvaise météo pour aller courir, seulement de mauvais équipements !
          </h2>
          <div className="text-gray-600 leading-relaxed space-y-4 text-justify">
            <p>
              En Auvergne, on connaît : la chaleur qui tape, le vent qui décoiffe, la pluie surprise, et ces matins d'hivers trop fréquents où on se demande si nos doigts vont survivre… Pas toujours simple de s'habiller pour courir !
            </p>
            <p>
              Heureusement, les vêtements techniques de running ont fait un véritable bond en avant ces dernières années. Plus légers, plus respirants et plus intelligents, ils vous permettent de courir confortablement par tous les temps, tout en vous protégeant des caprices de la météo.
            </p>
            <p>
              Nous sélectionnons pour vous des tenues innovantes au design soigné, pensées pour offrir le meilleur équilibre entre performance et plaisir.
            </p>
            <p>
              Grâce à des matières souples et légères, des coutures thermocollées et des découpes laser, vous profitez désormais d'un confort optimal, été comme hiver.
            </p>
            <p>
              Venez en discuter en magasin, toucher les matières, essayer les nouveautés, et trouver la tenue qui vous donnera envie de courir, quel que soit le temps !
            </p>
          </div>
        </div>
      </section>

      {/* Seasons Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <SectionTitle
            title="S'adapter à chaque saison"
            subtitle="Le bon équipement pour chaque condition météo"
          />
          <div className="grid md:grid-cols-3 gap-6 mt-10">
            {seasons.map((season) => {
              const IconComponent = season.icon;
              const styles = getCardStyles(season.cardStyle);
              return (
                <div 
                  key={season.title} 
                  className={`rounded-2xl p-8 ${styles.bg} transition-transform hover:scale-[1.02] duration-300`}
                >
                  <div className={`w-14 h-14 ${styles.iconBg} rounded-xl flex items-center justify-center mb-6`}>
                    <IconComponent className={`w-7 h-7 ${styles.iconColor}`} />
                  </div>
                  <h3 className={`font-neuropolitical font-bold text-2xl mb-4 ${styles.text}`}>
                    {season.title}
                  </h3>
                  <div className={`leading-relaxed whitespace-pre-line text-sm ${styles.subtext} text-justify`}>
                    {season.description}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Carousel Section */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <Carousel
            opts={{
              align: "start",
              loop: true,
            }}
            className="w-full"
          >
            <CarouselContent className="-ml-4">
              {textileCarouselImages.map((image, index) => (
                <CarouselItem key={index} className="pl-4 md:basis-1/2 lg:basis-1/3">
                  <div 
                    className="overflow-hidden rounded-xl shadow-lg cursor-pointer"
                    onClick={() => setSelectedImage(image)}
                  >
                    <img
                      src={image}
                      alt={`Textile running ${index + 1}`}
                      className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="-left-4 lg:-left-12" />
            <CarouselNext className="-right-4 lg:-right-12" />
          </Carousel>
        </div>
      </section>

      {/* Brands Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <SectionTitle
            title="Nos Marques Textile"
            subtitle="Les meilleures marques de vêtements techniques"
          />
          <div className="mt-10">
            <img
              src="/assets/images/textile-marques.jpg"
              alt="Nos marques textile"
              className="w-full max-w-4xl mx-auto rounded-xl shadow-lg"
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default TextilePage;
